import React, {useState, useEffect, useRef} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {EditorCommand, tools, imageCommand} from './EditorCommands';
import {useDropzone} from 'react-dropzone';

import {ImageService} from '../Services/ImageService';
import {useOnDrop} from '../Shared/useOnDrop';

interface EditorProps {
  content: string;
  onChange: (content: string) => void;
  imageService: ImageService;
}

export interface Selection {
  start: number;
  end: number;
}

interface EditorState {
  content: string;
  selection: Selection;
}

function Editor({
  content: originalContent,
  onChange,
  imageService,
}: EditorProps) {
  const [selection, setSelection] = useState<Selection>({start: 0, end: 0});
  const [content, setContent] = useState(originalContent);
  const textarea = useRef<HTMLTextAreaElement>(null);

  const applyCommand = (command: EditorCommand) => {
    const [newContent, newSelection] = command(content, selection);

    updateTextarea(newContent, newSelection);
  };

  const onUpload = (id: number) => {
    const url = imageService.link(id);
    const [newContent, newSelection] = imageCommand(content, selection, url);

    updateTextarea(newContent, newSelection);
  };

  const onDrop = useOnDrop(imageService, onUpload);
  const {getRootProps} = useDropzone({
    onDrop,
    accept: 'image/*',
    multiple: false,
  });

  useEffect(() => {
    const selectionUpdateEvents = [
      'select',
      'click',
      'focus',
      'keyup',
    ];

    const selectionUpdateListener = () => {
      if (textarea.current === undefined) {
        return;
      }

      const selection = getSelection(textarea.current!);

      setSelection(selection);
    };

    const addEventListeners = () => selectionUpdateEvents.forEach(
      eventType => textarea.current!.addEventListener(
        eventType, selectionUpdateListener,
      ),
    );
    addEventListeners();
    const currentRef = textarea.current!;

    return () => {
      const removeEventListeners = () => selectionUpdateEvents.forEach(
        eventType => currentRef.removeEventListener(
          eventType, selectionUpdateListener,
        ),
      );
      removeEventListeners();
    };
  }, []);

  const updateTextarea = (newContent?: string, newSelection?: Selection) => {
    if (textarea.current === undefined) {
      return;
    }

    const updatedContent = newContent || textarea.current!.value;
    const updatedSelection = newSelection || getSelection(textarea.current!);

    if (updatedContent !== content) {
      onChange(updatedContent);
    }

    setContent(updatedContent);
    setSelection(updatedSelection);
    setSelectionToDOM(textarea.current!, updatedSelection);
  };

  const handleChange = () => {
    updateTextarea(textarea.current!.value, getSelection(textarea.current!));
  };

  const getSelection = (ref: HTMLTextAreaElement): Selection => ({
    start: ref.selectionStart,
    end: ref.selectionEnd,
  });

  const setSelectionToDOM = (ref: HTMLTextAreaElement, selection: Selection) => {
    ref.selectionStart = selection.start;
    ref.selectionEnd = selection.end;
  };

  return (
    <div>
      <div className="toolbox">
        <ul>
          {tools.map(t => {
            return <li key={t.name} onClick={() => applyCommand(t.command)}><FontAwesomeIcon icon={t.icon} title={t.name} /></li>;
          })}
        </ul>
      </div>
      <div {...getRootProps()}>
        <textarea ref={textarea} className="form-control" value={content} onChange={handleChange} />
      </div>
    </div>
  );
}

export default Editor;
